import { ProfileResource, getReferenceString } from '@medplum/core';
import {
  Loading,
  NotificationIcon,
  useMedplum,
  useMedplumNavigate,
  useMedplumProfile,
} from '@medplum/react';
import {
  IconClipboardCheck,
  IconClipboardHeart,
  IconClipboardList,
  IconMail,
  IconMicroscope,
  IconUser,
  IconUsers,
  IconFlask,
  IconPlus,
  IconShield,
  IconUserPlus,
  IconCash,
} from '@tabler/icons-react';
import { Suspense } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { SignInPage } from './pages/authentication/SignInPage';
import { PatientPage } from './pages/patient/PatientPage';
import { PatientSearchPage } from './pages/patient/PatientSearchPage';
import { TaskTab } from './pages/patient/TaskTab';
import { ResourceCreatePage } from './pages/resource/ResourceCreatePage';
import { ResourceDetailPage } from './pages/resource/ResourceDetailPage';
import { ResourceEditPage } from './pages/resource/ResourceEditPage';
import { ResourceHistoryPage } from './pages/resource/ResourceHistoryPage';
import { ResourcePage } from './pages/resource/ResourcePage';
import LandingPage from './pages/ainigma/LandingPage';
import { ClientApplicationRoutes } from './pages/ainigma/client-application/ClientApplicationRoutes';
import { SignOutPage } from './pages/authentication/SignOutPage';
import logo from '/arkon-logo-small.png';
import { ClientApplicationLoader } from './pages/ainigma/client-application/ClientApplicationLoader';
import { PatientVisitHistory } from './pages/ainigma/patient/visits/PatientVisitHistory';
import { PatientNewVisit } from './pages/ainigma/patient/visits/PatientNewVisit';
import { useLanguageSelector } from '@hooks/useLanguageSelector';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useUserPreferences } from '@hooks/useUserPreferences';
import { AppShell } from '@medplum-components/AppShell/AppShell';
import { useTranslation } from 'react-i18next';
import './global.css';
import { TimelineTab } from './pages/patient/TimelineTab';
import { SummaryTab } from './pages/ainigma/patient/summary-tab/SummaryTab';
import { DiagnosticReportTimeline } from './pages/ainigma/patient/diagnostic-report-timeline/DiagnosticReportTimeline';
import { TaskSearchPage } from './pages/tasks/TaskSearchPage';
import { EncounterTab } from './pages/ainigma/patient/encounters-tab/EncounterTab';
import { CurrentEncounterTab } from './pages/ainigma/patient/current-encounter-tab/CurrentEncounterTab';
import { ImageTab } from './pages/ainigma/patient/images-tab/ImageTab';
import { PractitionersPage } from './pages/PractitionersPage';
import { PractitionerPage } from './pages/PractitionerPage';
import { EncounterPage } from './pages/EncounterPage';
import { AssignedDoctorsTab } from './pages/ainigma/patient/assigned-doctors-tab/AssignedDoctorsTab';
import OrderCreateStepper from './pages/ainigma/lims/orders/OrderCreateStepper';
import { SpecimensPage } from './pages/ainigma/lims/specimens/SpecimensPage';
import { SpecimensPageWithPatient } from './pages/ainigma/lims/specimens/SpecimensPageWithPatient';
import { SpecimenDetailsPage } from './pages/ainigma/lims/specimens/SpecimenDetailsPage';
import { DiagnosticReportDetail } from './pages/ainigma/lims/diagnostic-reports/DiagnosticReportDetail';
import { ReportEditorPage } from './pages/ainigma/lims/report-editor/ReportEditorPage';
import { AdminPage } from './pages/ainigma/admin/AdminPage';
import { UserManagementPage } from './pages/ainigma/admin/user-management/UserManagementPage';
import { useAccessPolicyContext } from '@providers/useAccessPolicyContext';
import { DiagnosesPageWithPatient } from './pages/ainigma/patient/diagnosis/DiagnosesPageWithPatient';
import { ResetPasswordPage } from './pages/authentication/ResetPasswordPage';
import { SetPasswordPage } from './pages/authentication/SetPasswordPage';
import { PatientOnboardingPage } from './pages/ainigma/patient-onboarding/PatientOnboardingPage';
import { useMyInProgressEncountersCount } from './hooks/useMyInProgressEncountersCount';
import { ReportFileTab } from './pages/ainigma/patient/files-tab/ReportFileTab';
import { OrdersListWithPatient } from './pages/ainigma/lims/orders/OrdersListWithPatient';
import { OrdersList } from './pages/ainigma/lims/orders/OrdersList';
import { OrderPage } from './pages/ainigma/lims/orders/OrderPage';
import { PaymentsPage } from './pages/ainigma/lims/payments/PaymentsPage';

export function App(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { t } = useTranslation(['common']);

  const location = useLocation();

  //----------------------------------------------------------------------------
  useUserPreferences(true);

  //----------------------------------------------------------------------------
  const navigate = useMedplumNavigate();

  // Get current user's access policy and component permissions
  const {
    componentAccess,
    isAdmin,
    loading: accessLoading,
  } = useAccessPolicyContext();

  const { selectedLanguage, changeLanguage } = useLanguageSelector();
  const isEnglish = selectedLanguage.code === 'en';

  const { count: myEncountersCount } = useMyInProgressEncountersCount(
    profile,
    componentAccess.canManageEncounters
  );

  if (medplum.isLoading()) {
    return null;
  }

  if (profile && (profile.resourceType as any) === 'ClientApplication') {
    return <ClientApplicationRoutes />;
  }

  const shouldHideHeaderPadding = location.pathname === '/' && !profile;

  // Show loading state while access permissions are being loaded
  if (accessLoading) {
    return <Loading />;
  }

  const buildMenus = () => {
    const menus = [];

    // Patient onboarding menu (always available)
    menus.push({
      title: t('layout.sidebar.patientOnboarding', { ns: 'common' }),
      links: [
        {
          icon: <IconUserPlus />,
          label: t('layout.sidebar.onboardPatient', { ns: 'common' }),
          href: '/patient-onboarding',
        },
      ],
    });

    if (componentAccess.canManageEncounters) {
      menus.push({
        title: t('layout.sidebar.encounters', { ns: 'common' }),
        links: [
          {
            icon: <IconClipboardHeart />,
            label: t('layout.sidebar.myEncounters', { ns: 'common' }),
            href: `/Encounter?participant=Practitioner/${profile?.id}&_fields=subject,type,status,period&_sort=-date`,
            count: myEncountersCount,
          },
          {
            icon: <IconClipboardList />,
            label: t('layout.sidebar.allEncounters', { ns: 'common' }),
            href: '/Encounter?_fields=subject,type,status,period,participant&_sort=-date,-_lastUpdated',
          },
        ],
      });
    }

    if (componentAccess.canAccessPatients) {
      const chartLinks = [];

      if (componentAccess.canAccessPatients) {
        chartLinks.push({
          icon: <IconUser />,
          label: t('layout.sidebar.patients', { ns: 'common' }),
          href: '/patients',
        });
      }

      if (componentAccess.canAccessPractitioners) {
        chartLinks.push({
          icon: <IconUsers />,
          label: t('layout.sidebar.practitioners', { ns: 'common' }),
          href: '/practitioners',
        });
      }

      if (chartLinks.length > 0) {
        menus.push({
          title: t('layout.sidebar.charts', { ns: 'common' }),
          links: chartLinks,
        });
      }
    }

    if (componentAccess.canAccessLims) {
      const limsLinks = [];

      limsLinks.push({
        icon: <IconFlask />,
        label: t('layout.sidebar.geneticTesting', { ns: 'common' }),
        href: '/lims',
      });

      if (componentAccess.canCreateOrders) {
        limsLinks.push({
          icon: <IconPlus />,
          label: t('layout.sidebar.newOrder', { ns: 'common' }),
          href: '/lims/new',
        });
      }

      if (componentAccess.canManageSpecimens) {
        limsLinks.push({
          icon: <IconMicroscope />,
          label: t('layout.sidebar.allSpecimens', { ns: 'common' }),
          href: '/lims/specimens',
        });
      }

      if (limsLinks.length > 0) {
        menus.push({
          title: t('layout.sidebar.laboratory', { ns: 'common' }),
          links: limsLinks,
        });
      }
    }

    if (componentAccess.canAccessPayments) {
      menus.push({
        title: t('layout.sidebar.billing', { ns: 'common' }),
        links: [
          {
            icon: <IconCash />,
            label: t('layout.sidebar.payments', { ns: 'common' }),
            href: '/lims/payments',
          },
        ],
      });
    }

    if (componentAccess.canAccessAdmin) {
      menus.push({
        title: t('layout.sidebar.administration', { ns: 'common' }),
        links: [
          {
            icon: <IconShield />,
            label: t('layout.sidebar.admin', { ns: 'common' }),
            href: '/admin',
          },
        ],
      });
    }

    return menus;
  };

  return (
    <AppShell
      logo={<img src={logo} alt="Logo" width="26" height="26" />}
      menus={buildMenus()}
      resourceTypeSearchDisabled
      notifications={
        profile && (
          <>
            <Tooltip label={isEnglish ? 'English' : 'Ελληνικά'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                onClick={() => changeLanguage(isEnglish ? 'el' : 'en')}
              >
                {isEnglish ? 'En' : 'Ελ'}
              </ActionIcon>
            </Tooltip>
            <NotificationIcon
              label="Mail"
              resourceType="Communication"
              countCriteria={`recipient=${getReferenceString(
                profile as ProfileResource
              )}&status:not=completed&_summary=count`}
              subscriptionCriteria={`Communication?recipient=${getReferenceString(
                profile as ProfileResource
              )}`}
              iconComponent={<IconMail />}
              onClick={() =>
                navigate(
                  `/Communication?recipient=${getReferenceString(
                    profile as ProfileResource
                  )}&status:not=completed&_fields=sender,recipient,subject,status,_lastUpdated`
                )
              }
            />
            <NotificationIcon
              label="Tasks"
              resourceType="Task"
              countCriteria={`owner=${getReferenceString(
                profile as ProfileResource
              )}&status:not=completed&_summary=count&_filter=period pr false or period lt ${new Date().toISOString()}`}
              // TODO: add owner to the criteria once we set the correct owner
              // countCriteria={`owner=${getReferenceString(
              //   profile as ProfileResource
              // )}&status:not=completed&_summary=count`}
              subscriptionCriteria={`Task`}
              // subscriptionCriteria={`Task?owner=${getReferenceString(
              //   profile as ProfileResource
              // )}`}
              iconComponent={<IconClipboardCheck />}
              onClick={() => navigate(`/Tasks`)}
              // onClick={() =>
              //   navigate(
              //     `/Task?owner=${getReferenceString(
              //       profile as ProfileResource
              //     )}&status:not=completed&_fields=subject,code,description,status,_lastUpdated`
              //   )
              // }
            />
          </>
        )
      }
      disableHeaderPadding={shouldHideHeaderPadding}
    >
      <Suspense fallback={<Loading />}>
        <Routes>
          {profile ? (
            <>
              <Route path="/" element={<PatientOnboardingPage />} />
              <Route path="/patients" element={<HomePage />} />
              <Route path="/Patient/:patientId" element={<PatientPage />}>
                <Route
                  path="diagnostic-report-timeline"
                  element={<DiagnosticReportTimeline />}
                />
                <Route
                  path="current-encounter"
                  element={<CurrentEncounterTab />}
                />
                <Route path="encounters" element={<EncounterTab />} />
                <Route path="images" element={<ImageTab />} />
                <Route path="files" element={<ReportFileTab />} />
                <Route
                  path="assigned-doctors"
                  element={<AssignedDoctorsTab />}
                />
                <Route path="orders" element={<OrdersListWithPatient />} />
                <Route
                  path="diagnosis"
                  element={<DiagnosesPageWithPatient />}
                />
                <Route
                  path="specimens"
                  element={<SpecimensPageWithPatient />}
                />
                {/* <Route path="Encounters/new" element={<EncounterModal />} />
                <Route
                  path="Encounter/:encounterId"
                  element={<EncounterChart />}
                >
                  <Route path="Task/:taskId" element={<TaskDetails />} />
                </Route> */}
                {/* <Route path="edit" element={<EditTab />} /> */}
                {/* <Route path="communication" element={<CommunicationTab />} />
                <Route
                  path="communication/:id"
                  element={<CommunicationTab />}
                /> */}
                {/* <Route path="task/:id/*" element={<TaskTab />} /> */}
                <Route path="timeline" element={<TimelineTab />} />
                <Route path=":resourceType" element={<PatientSearchPage />} />
                <Route
                  path=":resourceType/new"
                  element={<ResourceCreatePage />}
                />
                <Route path=":resourceType/:id" element={<ResourcePage />}>
                  <Route path="" element={<ResourceDetailPage />} />
                  <Route path="edit" element={<ResourceEditPage />} />
                  <Route path="history" element={<ResourceHistoryPage />} />
                </Route>
                <Route path="visits">
                  <Route path="new" element={<PatientNewVisit />} />
                  <Route path="" element={<PatientVisitHistory />} />
                </Route>
                <Route path="" element={<SummaryTab />} />
              </Route>
              <Route path="Tasks" element={<TaskSearchPage />} />
              <Route path="Task/:id/*" element={<TaskTab />} />
              <Route path="/practitioners" element={<PractitionersPage />} />
              <Route
                path="/Practitioner/:practitionerId"
                element={<PractitionerPage />}
              />
              <Route
                path="/Encounter/:encounterId"
                element={<EncounterPage />}
              />
              {/* LIMS Routes */} <Route path="lims" element={<OrdersList />} />
              <Route path="lims/new" element={<OrderCreateStepper />} />
              <Route path="lims/order/:id" element={<OrderPage />} />
              <Route
                path="lims/order/:orderId/diagnostic-report/:reportId"
                element={<DiagnosticReportDetail />}
              />
              <Route
                path="lims/order/:orderId/edit-report"
                element={<ReportEditorPage />}
              />
              <Route
                path="lims/order/:orderId/edit-report/:reportId"
                element={<ReportEditorPage />}
              />
              <Route path="lims/specimens" element={<SpecimensPage />} />
              <Route
                path="lims/specimen/:id"
                element={<SpecimenDetailsPage />}
              />
              <Route path="lims/payments" element={<PaymentsPage />} />
              {/* Patient Onboarding Route - Available to all users */}
              <Route
                path="/patient-onboarding"
                element={<PatientOnboardingPage />}
              />
              {/* Admin Routes - Only accessible to admin users */}
              {isAdmin && (
                <>
                  <Route path="/admin" element={<AdminPage />} />
                  <Route
                    path="/admin/user-management"
                    element={<UserManagementPage />}
                  />
                </>
              )}
              <Route path="/:resourceType" element={<SearchPage />} />
              <Route
                path="/:resourceType/new"
                element={<ResourceCreatePage />}
              />
              <Route path="/:resourceType/:id" element={<ResourcePage />}>
                <Route path="" element={<ResourceDetailPage />} />
                <Route path="edit" element={<ResourceEditPage />} />
                <Route path="history" element={<ResourceHistoryPage />} />
              </Route>
              <Route path="client/*" element={<SignOutPage />} />
            </>
          ) : (
            <>
              <Route path="/" element={<LandingPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/forgot-password" element={<ResetPasswordPage />} />
              <Route
                path="/setpassword/:id/:secret"
                element={<SetPasswordPage />}
              />
              <Route path="client/*" element={<ClientApplicationLoader />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </AppShell>
  );
}
